import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../components/ui/sonner';
import { Building2, Lock, Mail, Eye, EyeOff, Home, Users, ClipboardList } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ProgramManagerLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Call the login API
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid credentials');
      }

      // Check if user is a program manager or location manager
      if (data.user?.employer_role === 'program_manager' || 
          data.user?.employer_role === 'location_manager') {
        // Save auth data
        login(data.token, data.user);
        toast.success(`Welcome, ${data.user.full_name}!`);
        // Redirect to PM dashboard
        navigate('/pm-dashboard');
      } else if (data.user?.role === 'employer' && data.user?.employer_role === 'company_admin') {
        // Company admin trying to use PM login - redirect them to main login
        setError('Company Admins should use the main login. Redirecting...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('This login is for Program Managers only. Please use the appropriate login page.');
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl mb-6 shadow-xl">
            <Home className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Program Manager Portal</h1>
          <p className="text-purple-200">Sign in to manage your Group Home program</p>
        </div>

        {/* Login Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-200 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="your.email@company.com"
                  required
                  data-testid="pm-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-purple-200 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter your password"
                  required
                  data-testid="pm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-300 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-pink-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="pm-login-btn"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <Building2 className="w-5 h-5" />
                  Sign In to Portal
                </>
              )}
            </button>
          </form>

          {/* Features Preview */}
          <div className="mt-8 pt-6 border-t border-white/20">
            <p className="text-sm text-purple-300 text-center mb-4">As a Program Manager, you can:</p>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-3 bg-white/5 rounded-xl">
                <ClipboardList className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <span className="text-xs text-purple-200">Manage Shifts</span>
              </div>
              <div className="p-3 bg-white/5 rounded-xl">
                <Users className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <span className="text-xs text-purple-200">Assign Workers</span>
              </div>
              <div className="p-3 bg-white/5 rounded-xl">
                <Home className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                <span className="text-xs text-purple-200">Track Tasks</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-6 text-center">
          <p className="text-purple-300 text-sm">
            Company Admin?{' '}
            <a href="/login" className="text-purple-400 hover:text-white font-medium">
              Use main login
            </a>
          </p>
          <p className="text-purple-400/60 text-xs mt-4">
            Contact your employer if you need access credentials
          </p>
        </div>
      </div>
    </div>
  );
}
